import { useEffect } from 'react';
import debounce from 'lodash/debounce';

interface UseCallbackOnIdleArgs {
  time: number;
  enabled: boolean;
  callback: () => void;
}

/**
 * Hook which calls a callback when user is on idle state
 * inspired by: https://stackoverflow.com/questions/667555/how-to-detect-idle-time-in-javascript
 * @param {number} time idle period (in ms) until the callback can be called
 * @param {boolean} enabled enable event listeners for idle state
 * @param {function} callback function which will be called
 */
const useCallbackOnIdle = ({ time, enabled, callback }: UseCallbackOnIdleArgs) => {
  useEffect(() => {
    const debouncedCallback = debounce(callback, time);

    const startEvents = () => {
      document.addEventListener('mousemove', debouncedCallback);
      document.addEventListener('mousedown', debouncedCallback);
      document.addEventListener('keypress', debouncedCallback);
      document.addEventListener('touchmove', debouncedCallback);
      document.addEventListener('scroll', debouncedCallback);
    };

    const stopEvents = () => {
      document.removeEventListener('mousemove', debouncedCallback);
      document.removeEventListener('mousedown', debouncedCallback);
      document.removeEventListener('keypress', debouncedCallback);
      document.removeEventListener('touchmove', debouncedCallback);
      document.removeEventListener('scroll', debouncedCallback);
    };

    if (enabled) {
      startEvents();
      debouncedCallback();
    } else {
      stopEvents();
      debouncedCallback.cancel();
    }

    return () => {
      stopEvents();
      debouncedCallback.cancel();
    };
  }, [enabled, time, callback]);
};

export default useCallbackOnIdle;
